import React, { useState } from "react";
import { Search, FileHeart, Printer } from "lucide-react";
import { supabase, CLINIC_ID, APP_NAME } from "./supabase.js";
import { dateLabel, friendlyError } from "./lib/format.js";
import { canonicalDivision } from "./lib/division.js";

function fmt(value) {
  return value === null || value === undefined || value === "" ? "—" : value;
}

export function MedicalRecordsPage({ identity }) {
  const [term, setTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState("");

  async function handleSearch(e) {
    e?.preventDefault();
    if (!term.trim()) return;
    setSearching(true);
    setSearched(true);
    setError("");
    setSelectedClient(null);
    setHistory(null);
    try {
      const needle = term.trim();
      const { data: clients, error: searchError } = await supabase
        .from("clients")
        .select("id, name, phone, birth_date, address, client_code")
        .eq("clinic_id", CLINIC_ID)
        .or(
          `name.ilike.%${needle}%,phone.ilike.%${needle}%,id.ilike.%${needle}%,client_code.ilike.%${needle}%`,
        )
        .order("name")
        .limit(20);
      if (searchError) throw searchError;

      const ids = (clients || []).map((c) => c.id);
      const isPrivileged = /^(admin|kasir)$/i.test(identity?.role?.role_name || "");
      const staffDivision = canonicalDivision(identity?.staffMember?.division);
      const staffName = identity?.staffMember?.name || "";
      const isAssistant = /asisten|assistant/i.test(identity?.role?.role_name || "");
      let lastVisitByClient = {};
      let visibleClients = clients || [];
      if (ids.length) {
        let visitQuery = supabase
          .from("visits")
          .select("client_id, visit_number, visit_date")
          .eq("clinic_id", CLINIC_ID)
          .in("client_id", ids)
          .order("visit_date", { ascending: false });
        if (!isPrivileged && staffDivision && staffDivision !== "ALL") {
          visitQuery = visitQuery.eq("division", staffDivision);
          if (staffName && !isAssistant) visitQuery = visitQuery.eq("doctor_name", staffName);
        } else if (!isPrivileged && !staffDivision) {
          visitQuery = visitQuery.eq("id", "__no_access__");
        }
        const { data: visits, error: visitError } = await visitQuery;
        if (visitError) throw visitError;
        (visits || []).forEach((v) => {
          if (!lastVisitByClient[v.client_id]) lastVisitByClient[v.client_id] = v;
        });
        if (!isPrivileged) {
          const visibleIds = new Set((visits || []).map((v) => v.client_id));
          visibleClients = visibleClients.filter((client) => visibleIds.has(client.id));
        }
      } else if (!isPrivileged && !staffDivision) {
        visibleClients = [];
      }

      setResults(
        visibleClients.map((c) => ({
          ...c,
          lastVisit: lastVisitByClient[c.id] || null,
        })),
      );
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSearching(false);
    }
  }

  function resetSearch() {
    setTerm("");
    setResults([]);
    setSearched(false);
    setSelectedClient(null);
    setHistory(null);
    setError("");
  }

  async function openHistory(client) {
    setSelectedClient(client);
    setHistory(null);
    setLoadingHistory(true);
    setError("");
    try {
      const isPrivileged = /^(admin|kasir)$/i.test(identity?.role?.role_name || '');
      const staffDivision = canonicalDivision(identity?.staffMember?.division);
      const staffName = identity?.staffMember?.name || '';
      const isAssistant = /asisten|assistant/i.test(identity?.role?.role_name || '');
      let visitQuery = supabase
        .from("visits")
        .select(
          "id, visit_number, visit_date, visit_status, doctor_name, room_name, payment_status, division",
        )
        .eq("clinic_id", CLINIC_ID)
        .eq("client_id", client.id)
        .order("visit_date", { ascending: false })
        .limit(50);
      if (!isPrivileged && staffDivision && staffDivision !== "ALL") {
        visitQuery = visitQuery.eq("division", staffDivision);
        if (staffName && !isAssistant) visitQuery = visitQuery.eq("doctor_name", staffName);
      } else if (!isPrivileged && !staffDivision) {
        visitQuery = visitQuery.eq("id", "__no_access__");
      }
      const { data: visits, error: visitError } = await visitQuery;
      if (visitError) throw visitError;

      const visitIds = (visits || []).map((v) => v.id);
      let notesByVisit = {};
      if (visitIds.length) {
        const { data: notes, error: notesError } = await supabase
          .from("medical_notes")
          .select("*")
          .eq("clinic_id", CLINIC_ID)
          .in("visit_id", visitIds);
        if (notesError) throw notesError;
        (notes || []).forEach((n) => {
          notesByVisit[n.visit_id] = n;
        });
      }

      setHistory(
        (visits || [])
          .filter((v) => /^selesai$/i.test((v.visit_status || "").trim()))
          .map((v) => ({ ...v, note: notesByVisit[v.id] || null })),
      );
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoadingHistory(false);
    }
  }

  return (
    <div className="page-stack">
      <article className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">Validasi Data XV</span>
            <h3>Rekam Medis Klien</h3>
          </div>
        </div>
        <p className="muted">
          Cari klien berdasarkan nama, nomor telepon, atau kode klien. Riwayat
          rekam medis dimuat setelah klien dipilih.
        </p>
        <form className="mr-search-row" onSubmit={handleSearch}>
          <div className="mr-search-input">
            <Search size={18} />
            <input
              type="text"
              placeholder="Contoh: Yani / 8129 / CLI-..."
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
          <button type="submit" className="button button-primary" disabled={searching}>
            {searching ? "Mencari…" : "Cari Klien"}
          </button>
          <button type="button" className="button button-quiet" onClick={resetSearch}>
            Reset
          </button>
        </form>
        <p className="muted small">
          Riwayat ditampilkan sesuai kewenangan poli dan nakes yang menangani.
          Admin dan Kasir dapat melihat riwayat lintas poli.
        </p>
      </article>

      {error && (
        <div className="alert alert-danger">
          <strong>Terjadi kesalahan.</strong> {error}
        </div>
      )}

      {searched && !selectedClient && (
        <article className="panel">
          <div className="panel-head">
            <h3>Hasil Pencarian</h3>
          </div>
          {results.length ? (
            <div className="compact-list">
              {results.map((c) => (
                <div className="compact-row" key={c.id}>
                  <div className="grow">
                    <strong>{c.name}</strong>
                    <span>
                      {c.id} • {fmt(c.phone)}
                    </span>
                    <span>
                      Tanggal lahir: {fmt(c.birth_date)} · Alamat:{" "}
                      {fmt(c.address)}
                    </span>
                    <span>
                      Kunjungan terakhir:{" "}
                      {c.lastVisit
                        ? `${dateLabel(c.lastVisit.visit_date)} • ${c.lastVisit.visit_number}`
                        : "—"}
                    </span>
                  </div>
                  <button
                    className="button button-mini button-primary"
                    onClick={() => openHistory(c)}
                  >
                    Pilih
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">Tidak ada klien yang cocok dengan pencarian.</p>
          )}
        </article>
      )}

      {selectedClient && (
        <article className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">Klien Terpilih</span>
              <h3>{selectedClient.name}</h3>
              <p className="muted">
                {selectedClient.id} • {fmt(selectedClient.phone)}
              </p>
            </div>
            <div className="mr-actions">
              {history && history.length ? (
                <button
                  className="button button-quiet"
                  onClick={() => window.print()}
                >
                  <Printer size={16} /> Cetak
                </button>
              ) : null}
              <button
                className="button button-quiet"
                onClick={() => {
                  setSelectedClient(null);
                  setHistory(null);
                }}
              >
                Ganti Klien
              </button>
            </div>
          </div>
        </article>
      )}

      {selectedClient && (
        <article className="panel">
          <div className="panel-head">
            <div>
              <FileHeart size={18} />
              <h3 style={{ display: "inline", marginLeft: 8 }}>
                Riwayat Kunjungan
              </h3>
            </div>
          </div>
          {loadingHistory ? (
            <div className="content-loading">
              <div className="loader" />
              <span>Memuat riwayat…</span>
            </div>
          ) : history && history.length ? (
            <div className="record-list">
              {history.map((v) => (
                <div className="record-card" key={v.id}>
                  <div className="record-head">
                    <strong>
                      {dateLabel(v.visit_date)} • {v.visit_number}
                    </strong>
                    <span className="muted">
                      Dokter: {fmt(v.doctor_name)} · Ruang: {fmt(v.room_name)}
                    </span>
                  </div>
                  <div className="record-grid">
                    <div className="record-field record-field-wide">
                      <strong>Keluhan</strong>
                      <span>{fmt(v.note?.current_complaint)}</span>
                    </div>
                    <div className="record-field">
                      <strong>Tensi/BPM</strong>
                      <span>
                        {fmt(v.note?.blood_pressure)} / {fmt(v.note?.bpm)}
                      </span>
                    </div>
                    <div className="record-field">
                      <strong>Nadi TCM</strong>
                      <span>{fmt(v.note?.tcm_pulse)}</span>
                    </div>
                    <div className="record-field">
                      <strong>Lidah TCM</strong>
                      <span>{fmt(v.note?.tcm_tongue)}</span>
                    </div>
                    <div className="record-field">
                      <strong>Kategori Sistem</strong>
                      <span>{fmt(v.note?.tcm_system_category)}</span>
                    </div>
                    <div className="record-field record-field-wide">
                      <strong>Pemicu/Pemberat/Pereda</strong>
                      <span>{fmt(v.note?.tcm_modifiers)}</span>
                    </div>
                    <div className="record-field record-field-wide">
                      <strong>Data Pendukung/Penyanggah</strong>
                      <span>{fmt(v.note?.tcm_supporting_data)}</span>
                    </div>
                    <div className="record-field">
                      <strong>Status Sindrom</strong>
                      <span>{fmt(v.note?.syndrome_status)}</span>
                    </div>
                    <div className="record-field">
                      <strong>Perbaikan</strong>
                      <span>{fmt(v.note?.improvement_status)}</span>
                    </div>
                    <div className="record-field record-field-wide">
                      <strong>Catatan</strong>
                      <span>{fmt(v.note?.action_notes)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">Belum ada riwayat kunjungan selesai untuk klien ini.</p>
          )}
        </article>
      )}

      {selectedClient && history && history.length ? (
        <div className="print-area">
          <h2>Rekam Medis</h2>
          <p className="print-clinic">{APP_NAME}</p>
          <div className="print-client">
            <div>
              <strong>Nama Klien:</strong> {selectedClient.name}
            </div>
            <div>
              <strong>Kode Klien:</strong> {selectedClient.id}
            </div>
            <div>
              <strong>Telepon:</strong> {fmt(selectedClient.phone)}
            </div>
          </div>
          <h3>Riwayat Kunjungan</h3>
          {history.map((v) => (
            <div className="print-record" key={v.id}>
              <div className="print-record-head">
                {dateLabel(v.visit_date)} • {v.visit_number} · Dokter:{" "}
                {fmt(v.doctor_name)} · Ruang: {fmt(v.room_name)}
              </div>
              <div>
                <strong>Keluhan:</strong> {fmt(v.note?.current_complaint)}
              </div>
              <div>
                <strong>Tensi/BPM:</strong> {fmt(v.note?.blood_pressure)} /{" "}
                {fmt(v.note?.bpm)}
              </div>
              <div>
                <strong>Nadi TCM:</strong> {fmt(v.note?.tcm_pulse)}
              </div>
              <div>
                <strong>Lidah TCM:</strong> {fmt(v.note?.tcm_tongue)}
              </div>
              <div>
                <strong>Kategori Sistem:</strong>{" "}
                {fmt(v.note?.tcm_system_category)}
              </div>
              <div>
                <strong>Pemicu/Pemberat/Pereda:</strong>{" "}
                {fmt(v.note?.tcm_modifiers)}
              </div>
              <div>
                <strong>Data Pendukung/Penyanggah:</strong>{" "}
                {fmt(v.note?.tcm_supporting_data)}
              </div>
              <div>
                <strong>Status Sindrom:</strong>{" "}
                {fmt(v.note?.syndrome_status)}
              </div>
              <div>
                <strong>Perbaikan:</strong> {fmt(v.note?.improvement_status)}
              </div>
              <div>
                <strong>Catatan:</strong> {fmt(v.note?.action_notes)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <style>{`
        .mr-actions {
          display: flex;
          gap: 8px;
        }
        .print-area {
          display: none;
        }
        @media print {
          .page-stack > .print-area {
            display: block !important;
            width: 100% !important;
            min-height: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            background: white !important;
          }
          .print-area,
          .print-area * {
            visibility: visible !important;
          }
          .print-area h2 {
            margin-bottom: 2px;
          }
          .print-clinic {
            color: #555;
            margin-top: 0;
            margin-bottom: 16px;
            border-bottom: 1px solid #333;
            padding-bottom: 12px;
          }
          .print-client {
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-bottom: 18px;
            font-size: 13px;
          }
          .print-record {
            border: 1px solid #ccc;
            border-radius: 6px;
            padding: 10px 14px;
            margin-bottom: 12px;
            font-size: 12px;
            page-break-inside: avoid;
          }
          .print-record-head {
            font-weight: 700;
            margin-bottom: 6px;
            padding-bottom: 6px;
            border-bottom: 1px solid #ddd;
          }
        }
        .mr-search-row {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
          margin: 14px 0;
        }
        .mr-search-input {
          flex: 1;
          min-width: 240px;
          display: flex;
          align-items: center;
          gap: 10px;
          background: #ffffff;
          border: 1px solid #e8e2d5;
          border-radius: 999px;
          padding: 10px 18px;
          box-shadow: 0 1px 2px rgba(20, 20, 10, 0.04);
          color: #a39c8a;
        }
        .mr-search-input input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 14px;
          color: #2b2a26;
          background: transparent;
        }
        .mr-search-input input::placeholder {
          color: #b8b1a0;
        }
        .record-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .record-card {
          border: 1px solid #e8e2d5;
          border-radius: 12px;
          padding: 16px 18px;
          background: #fffdf8;
        }
        .record-head {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          flex-wrap: wrap;
          gap: 4px 12px;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #efe9dc;
        }
        .record-head strong {
          font-size: 15px;
        }
        .record-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px 24px;
        }
        .record-field {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 13px;
        }
        .record-field-wide {
          grid-column: 1 / -1;
        }
        .record-field strong {
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #7a7364;
        }
        .record-field span {
          color: #2b2a26;
        }
        @media (max-width: 640px) {
          .record-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
